import { CommunityId, Task, User, UserId } from "@sp-crm/core";
import { useQuery } from "@tanstack/react-query";
import { ListClient } from "components/clients/list-client";
import { ClientForDetails } from "components/dashboard/types";
import { trpc } from "components/rpc/trpc";
import { Spinner } from "components/ui/spinner";
import {
    GetNextTasksForParentIdsQuery,
    ReferralBasicsFragment,
    useGetNextTasksForParentIdsQuery,
} from "generated/graphql";
import React, { useMemo } from "react";
import { ResponsiveMode } from "store/reducers/responsive";

type NextTasks = GetNextTasksForParentIdsQuery["getNextTasksForParentIds"];

const toGqlClient = (
    client: ClientForDetails,
    nextTasks: NextTasks,
): ReferralBasicsFragment["client"] => {
    const nextTask = nextTasks.find(t => t.parentId === client.id) || null;
    const loadedNextTask = nextTask ? Task.load(nextTask) : null;

    return {
        id: client.id,
        name: client.name,
        statusListItemId: client.statusListItemId,
        serial: client.serial,
        assignedUserId: client.assignedUserId,
        nextTask: loadedNextTask
            ? {
                  id: null,
                  status: loadedNextTask.active() ? "inprogress" : "done",
                  text: loadedNextTask.description(),
                  dueDateTime: loadedNextTask.when(),
                  compositeDueDateTime: loadedNextTask.when(),
              }
            : null,
        bestContactName: client.bestContactName || "",
        moveTimeline: client.moveTimeline?.toString() || "",
        moveTimelineActual: client.moveTimelineActual?.toString() || "",
        dateAdded: client.dateAdded,
        updatedAt: client.updatedAt,
    };
};

interface CommunityPlacementHistoryProps {
    responsiveMode: ResponsiveMode;
    users: Record<UserId, User>;
    communityId: CommunityId;
}

export const CommunityPlacementHistory: React.FC<
    CommunityPlacementHistoryProps
> = props => {
    const { responsiveMode, users, communityId } = props;

    const trpcClient = trpc.useTRPC();
    const clientsQuery = useQuery({
        ...trpcClient.clients.getClientsForCommunity.queryOptions({ communityId }),
    });

    const clients = useMemo(() => clientsQuery.data || [], [clientsQuery.data]);
    const clientIds = useMemo(() => clients.map(c => c.id), [clients]);

    const getNextTasks = useGetNextTasksForParentIdsQuery({ parentIds: clientIds });
    const nextTasks = getNextTasks.data?.getNextTasksForParentIds || [];

    if (clientsQuery.isLoading) {
        return (
            <div className="flex justify-center py-8">
                <Spinner />
            </div>
        );
    }

    return (
        <section className="list-reference">
            <ListClient
                responsiveMode={responsiveMode}
                users={users}
                clients={clients.map(c => toGqlClient(c, nextTasks))}
                sourceType={"fromCommunity"}
                showAssignedToColumn={false}
            />
        </section>
    );
};
